import React, { useState } from 'react';
import { Plus, Minus, HelpCircle } from 'lucide-react';

interface FaqItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

const FaqItem: React.FC<FaqItemProps> = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div className="border-b border-gray-200 py-6 last:border-b-0">
      <button
        className="flex justify-between items-center w-full text-right focus:outline-none group"
        onClick={onToggle}
      >
        <h3 className="text-xl font-semibold text-gray-800 group-hover:text-blue-800 transition-colors pr-4">
          {question}
        </h3>
        <span className="flex-shrink-0">
          {isOpen ? 
            <Minus className="h-6 w-6 text-blue-800" /> : 
            <Plus className="h-6 w-6 text-blue-800" />
          }
        </span>
      </button>
      {isOpen && (
        <div className="mt-4 text-gray-600 leading-relaxed text-lg animate-fade-in">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

const Faq = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: "كيف يمكنني حجز جلسة تدريس؟",
      answer: "حجز جلسة تدريس سهل جداً. يمكنك التواصل معنا عبر الواتساب أو الهاتف على الرقم 0590317360، أو ملء نموذج الاتصال على موقعنا. سنرد عليك في أقرب وقت لترتيب وقت مناسب لجلستك التعليمية."
    },
    {
      question: "كم مدة كل جلسة تدريس؟",
      answer: "مدة كل جلسة تدريس قياسية هي ساعة واحدة كاملة. ومع ذلك، يمكننا ترتيب جلسات أطول أو جلسات متعددة في الأسبوع بناءً على احتياجاتك وأهدافك التعليمية المحددة."
    },
    {
      question: "هل الحصص متاحة مسجلة للمراجعة؟",
      answer: "نقدم حصص تفاعلية مباشرة مع المدرس لضمان أفضل تفاعل وفهم. كما نوفر الحصة الأولى مجاناً لجميع الطلاب الجدد لتجربة خدماتنا."
    },
    {
      question: "ما هي المؤهلات التي يحملها المدرسون؟",
      answer: "جميع مدرسينا حاصلون على شهادات جامعية متخصصة في المواد التي يدرسونها، بالإضافة إلى مؤهلات تربوية معتمدة. لديهم سنوات من الخبرة في التدريس عن بُعد ومهارات متقدمة في تبسيط المعلومات المعقدة."
    },
    {
      question: "هل تقدمون جلسات تدريس جماعية؟",
      answer: "نعم، نقدم جلسات تدريس جماعية للطلاب الذين يدرسون نفس المادة وفي نفس المستوى التعليمي. تُقدم الجلسات الجماعية بأسعار مخفضة، مما يجعل التعليم الجيد في متناول الجميع."
    },
    {
      question: "كيف يعمل عرض الـ 8 حصص؟",
      answer: "عند حجز 8 حصص معاً، تحصل على تخفيض في سعر كل حصة. هذا العرض يوفر لك المال ويضمن استمرارية التعلم. يمكنك استخدام الحصص على مدى شهرين، مما يعطيك مرونة في التخطيط لدراستك."
    }
  ];

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-4">
        {/* العنوان الرئيسي */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-blue-100 p-4 rounded-full ml-4">
              <HelpCircle className="h-10 w-10 text-blue-800" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              الأسئلة الشائعة
            </h2>
          </div>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            اعثر على إجابات للأسئلة الشائعة حول خدماتنا التعليمية وكيفية الاستفادة منها بأفضل طريقة.
          </p>
        </div>
        
        {/* قائمة الأسئلة */}
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
          {faqs.map((faq, index) => (
            <FaqItem 
              key={index} 
              question={faq.question} 
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => toggleFaq(index)}
            />
          ))}
        </div>
        
        {/* قسم التواصل */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-10 rounded-2xl max-w-3xl mx-auto border border-blue-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              لم تجد سؤالك هنا؟
            </h3>
            <p className="text-gray-600 mb-8 text-lg">
              فريق خدمة العملاء لدينا جاهز للإجابة على جميع استفساراتك
            </p>
            <a 
              href="https://wa.me/+9660590317360"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-4 bg-blue-800 hover:bg-blue-900 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg text-lg"
            >
              تواصل معنا الآن
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Faq;